import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, ClipboardCheck, AlertTriangle, CheckCircle } from 'lucide-react';
import MainHeader from '../components/MainHeader';
import MainFooter from '../components/MainFooter';
import ScopeBoundary from '../components/ScopeBoundary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function GovernanceChecklist() {
  const [step, setStep] = useState('form'); // form, email, results
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const questions = [
    {
      category: 'Documentation',
      items: [
        { id: 'q1', text: 'Do you have a written AI Acceptable Use Policy?' },
        { id: 'q2', text: 'Do you maintain an inventory of all AI tools in use?' },
        { id: 'q3', text: 'Do employees sign an AI use acknowledgment or agreement?' },
      ]
    },
    {
      category: 'Oversight',
      items: [
        { id: 'q4', text: 'Is there a designated person responsible for AI governance in your organization?' },
        { id: 'q5', text: 'Do you have a process for approving new AI tools before adoption?' },
        { id: 'q6', text: 'Are AI-generated outputs reviewed by a human before reaching clients or affecting decisions?' },
      ]
    },
    {
      category: 'Risk Management',
      items: [
        { id: 'q7', text: 'Have you assessed the risks of each AI tool your organization uses?' },
        { id: 'q8', text: 'Do you conduct vendor risk assessments for third-party AI tools?' },
        { id: 'q9', text: 'Do you have a process for handling AI errors or unexpected outputs?' },
      ]
    },
    {
      category: 'Accountability',
      items: [
        { id: 'q10', text: 'Do you have an AI Incident Log for documenting AI-related issues?' },
        { id: 'q11', text: 'Has your leadership team reviewed your organization\'s AI governance posture in the last 12 months?' },
        { id: 'q12', text: 'Can you demonstrate your AI governance practices to a client or auditor if asked today?' },
      ]
    }
  ];

  const allQuestionIds = questions.flatMap(cat => cat.items.map(q => q.id));

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const answered = Object.keys(answers).length;
    if (answered < allQuestionIds.length) {
      setError('Please answer all questions');
      return;
    }
    setError('');
    setStep('email');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/tools/governance-checklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          answers,
          source_tool: 'governance-checklist'
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResult(data);
        setStep('results');
      } else {
        setError(data.detail || 'Failed to generate checklist results');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score <= 8) return { bg: '#FEF2F2', border: '#FECACA', text: '#B91C1C', label: 'Critical Gaps' };
    if (score <= 16) return { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', label: 'Developing' };
    return { bg: '#F0FDF4', border: '#BBF7D0', text: '#15803D', label: 'Strong Foundation' };
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <MainHeader />
      
      <main className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/free-tools" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#102A43] mb-8 text-sm">
            <ArrowLeft size={16} />
            Back to Free Tools
          </Link>

          {step === 'form' && (
            <>
              <div className="text-center mb-12">
                <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4">
                  FREE TOOL
                </p>
                <h1 className="text-[36px] font-bold text-[#102A43] mb-4 leading-tight">
                  Get your AI governance readiness checklist — free
                </h1>
                <p className="text-[16px] text-[#64748B]">
                  Answer 12 questions and get a gap report showing exactly which governance areas need attention.
                </p>
              </div>

              <form onSubmit={handleFormSubmit}>
                {questions.map((category) => (
                  <div key={category.category} className="bg-white border border-[#E2E8F0] rounded-[10px] p-6 mb-6">
                    <h2 className="text-[18px] font-bold text-[#102A43] mb-4">{category.category}</h2>
                    <div className="space-y-4">
                      {category.items.map((question) => (
                        <div key={question.id} className="border-b border-[#E2E8F0] pb-4 last:border-0 last:pb-0">
                          <p className="text-[14px] text-[#102A43] mb-3">{question.text}</p>
                          <div className="flex gap-2">
                            {['Yes', 'Partial', 'No'].map((option) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => handleAnswer(question.id, option)}
                                className={`px-4 py-2 text-sm rounded-md border transition-colors ${
                                  answers[question.id] === option
                                    ? option === 'Yes' 
                                      ? 'bg-[#15803D] text-white border-[#15803D]'
                                      : option === 'Partial'
                                        ? 'bg-[#92400E] text-white border-[#92400E]'
                                        : 'bg-[#B91C1C] text-white border-[#B91C1C]'
                                    : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#2F80ED]'
                                }`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

                <button
                  type="submit"
                  className="w-full bg-[#2F80ED] text-white font-semibold py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors flex items-center justify-center gap-2"
                  data-testid="checklist-continue-btn"
                >
                  Continue
                  <ArrowRight size={18} />
                </button>
              </form>
            </>
          )}

          {step === 'email' && (
            <>
              <div className="text-center mb-12">
                <h1 className="text-[32px] font-bold text-[#102A43] mb-4">
                  Where should we send your results?
                </h1>
                <p className="text-[16px] text-[#64748B]">
                  Enter your email to receive your AI Governance Checklist results and gap report.
                </p>
              </div>

              <form onSubmit={handleEmailSubmit} className="bg-white border border-[#E2E8F0] rounded-[10px] p-8">
                <div>
                  <label className="block text-sm font-medium text-[#102A43] mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                    placeholder="you@company.com"
                    required
                    data-testid="checklist-email"
                  />
                </div>

                {error && <p className="text-red-600 text-sm mt-4">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-6 bg-[#2F80ED] text-white font-semibold py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors disabled:opacity-50"
                  data-testid="checklist-submit-btn"
                >
                  {loading ? 'Processing...' : 'Get My Results'}
                </button>

                <button
                  type="button"
                  onClick={() => setStep('form')}
                  className="w-full mt-3 text-[#64748B] hover:text-[#102A43] text-sm"
                  data-testid="checklist-back-btn"
                >
                  ← Back to questions
                </button>
              </form>
            </>
          )}

          {step === 'results' && result && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-[#F0FDF4] rounded-full flex items-center justify-center mx-auto mb-4">
                  <ClipboardCheck className="text-[#15803D]" size={32} />
                </div>
                <h1 className="text-[32px] font-bold text-[#102A43] mb-2">
                  Your AI Governance Checklist Results
                </h1>
              </div>

              {/* Score Card */}
              <div 
                className="rounded-[10px] p-8 mb-8 text-center"
                style={{ 
                  background: getScoreColor(result.score).bg,
                  border: `1px solid ${getScoreColor(result.score).border}`
                }}
              >
                <p className="text-[48px] font-bold mb-2" style={{ color: getScoreColor(result.score).text }}>
                  {result.score} / 24
                </p>
                <p className="text-[18px] font-semibold" style={{ color: getScoreColor(result.score).text }}>
                  {getScoreColor(result.score).label}
                </p>
              </div>

              {/* Gap Summary */}
              {result.gaps && result.gaps.length > 0 && (
                <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6 mb-6">
                  <h2 className="text-[18px] font-bold text-[#102A43] mb-4 flex items-center gap-2">
                    <AlertTriangle className="text-[#B91C1C]" size={20} />
                    Gap Summary ({result.gaps.length} areas need attention)
                  </h2>
                  <ul className="space-y-2">
                    {result.gaps.map((gap, i) => (
                      <li key={i} className="flex items-start gap-2 text-[14px] text-[#64748B]">
                        <span className="text-[#B91C1C]">•</span>
                        {gap}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Download & Next Steps */}
              <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-8 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-[18px] font-bold text-[#102A43]">
                      Full Results Report
                    </h2>
                    <p className="text-[14px] text-[#64748B]">
                      PDF document · Sent to {email}
                    </p>
                  </div>
                  {result.download_url && (
                    <a
                      href={result.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-5 py-2 rounded-[7px] hover:bg-[#2563eb] transition-colors"
                    >
                      <Download size={18} />
                      Download PDF
                    </a>
                  )}
                </div>

                <ScopeBoundary
                  delivers={[
                    'A score out of 24 across 12 governance areas',
                    'A gap report showing which areas scored zero or partial',
                    'A prioritized top-3 action list'
                  ]}
                  doesNotDeliver={[
                    'Any templates needed to close the gaps — the checklist is a diagnostic, not a solution'
                  ]}
                />

                <div className="bg-[#F9FAFB] border border-[#E2E8F0] rounded-[10px] p-6">
                  <p className="text-[15px] text-[#102A43] mb-4">
                    Your checklist shows <strong>{result.gaps?.length || 0} gaps</strong> across your AI governance posture. Get a complete assessment with gap analysis and a prioritized 30-day action plan.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/assessment-product"
                      className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors"
                    >
                      Get Your Full Assessment — $297
                      <ArrowRight size={18} />
                    </Link>
                    <Link
                      to="/documentation-system"
                      className="inline-flex items-center gap-2 border border-[#E2E8F0] text-[#64748B] font-semibold px-6 py-3 rounded-[7px] hover:border-[#2F80ED] hover:text-[#2F80ED] transition-colors"
                    >
                      Or start with Documentation — $97
                    </Link>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <MainFooter />
    </div>
  );
}

export default GovernanceChecklist;
